import React, { useState, useEffect } from 'react';
import { practiceService, availabilityService, dateTimeHelpers } from '../../models/database';
import { getAuth } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';

const AvailabilityManagement = () => {
  const [practices, setPractices] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState('');
  const [selectedOperative, setSelectedOperative] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Default time settings
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);
  const [slotDuration, setSlotDuration] = useState(30);
  
  // Recurring slots settings
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState(7); // Number of days to create recurring slots
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  });
  
  const auth = getAuth();

  // Fetch practices on component mount
  useEffect(() => {
    fetchPractices();
    
    // For demo purposes, we'll add a mock operative (the current user)
    // In a real app, you would fetch operatives from the database
    const currentUser = auth.currentUser;
    if (currentUser) {
      setOperatives([
        {
          id: currentUser.uid,
          displayName: currentUser.displayName || currentUser.email,
          email: currentUser.email,
        },
      ]);
    } else {
      // Mock operative for demo
      setOperatives([
        {
          id: 'mock-operative-1',
          displayName: 'Dr. John Smith',
          email: 'john.smith@dentalhero.com',
        },
      ]);
    }
  }, [auth]);

  // Fetch all practices
  const fetchPractices = async () => {
    try {
      setLoading(true);
      const practicesData = await practiceService.getAllPractices();
      setPractices(practicesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle practice selection
  const handlePracticeChange = (e) => {
    setSelectedPractice(e.target.value);
  };

  // Handle operative selection
  const handleOperativeChange = (e) => {
    setSelectedOperative(e.target.value);
  };

  // Handle date selection
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    
    // If we have all required selections, generate slots
    if (selectedPractice && selectedOperative && e.target.value) {
      generateAvailabilitySlots(selectedPractice, selectedOperative, e.target.value);
    }
  };

  // Generate availability slots for the selected date
  const generateAvailabilitySlots = async (practiceId, operativeId, dateString) => {
    try {
      setLoading(true);
      
      // Parse the date string (format: YYYY-MM-DD)
      const [year, month, day] = dateString.split('-').map(Number);
      
      // Create a date timestamp for the selected date
      const dateTimestamp = dateTimeHelpers.createDateTimestamp(year, month, day);
      
      // Check if availability already exists for this date, practice, and operative
      const existingAvailability = await availabilityService.getPracticeAvailability(practiceId, dateTimestamp);
      
      const operativeAvailability = existingAvailability.find(
        (avail) => avail.operativeId === operativeId
      );
      
      if (operativeAvailability) {
        // Use existing availability slots
        setAvailabilitySlots(operativeAvailability.slots.map((slot, index) => ({
          id: index,
          startTime: slot.startTime,
          endTime: slot.endTime,
          isAvailable: !slot.isBooked,
        })));
      } else {
        // Generate new availability slots
        const slots = dateTimeHelpers.generateTimeSlots(
          dateTimestamp,
          startHour,
          endHour,
          slotDuration
        );
        
        setAvailabilitySlots(
          slots.map((slot, index) => ({
            id: index,
            startTime: slot.startTime,
            endTime: slot.endTime,
            isAvailable: true,
          }))
        );
      }
      
      setError(null);
    } catch (err) {
      console.error('Error generating availability slots:', err);
      setError('Failed to generate availability slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle slot availability
  const toggleSlotAvailability = (slotId) => {
    setAvailabilitySlots(
      availabilitySlots.map((slot) =>
        slot.id === slotId ? { ...slot, isAvailable: !slot.isAvailable } : slot
      )
    );
  };

  // Format time for display (HH:MM AM/PM)
  const formatTime = (timestamp) => {
    const date = timestamp.toDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  };

  // Handle recurring days of week change
  const handleRecurringDayChange = (day) => {
    setRecurringDaysOfWeek(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };
  
  // Save availability
  const saveAvailability = async () => {
    if (!selectedPractice || !selectedOperative || !selectedDate) {
      setError('Please select a practice, operative, and date.');
      return;
    }
    
    try {
      setLoading(true);
      
      if (isRecurring) {
        // Save recurring availability
        await saveRecurringAvailability();
      } else {
        // Save single day availability
        await saveSingleDayAvailability();
      }
      
      setSuccess('Availability saved successfully!');
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error saving availability:', err);
      setError('Failed to save availability. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Save availability for a single day
  const saveSingleDayAvailability = async () => {
    // Parse the date string (format: YYYY-MM-DD)
    const [year, month, day] = selectedDate.split('-').map(Number);
    
    // Create a date timestamp for the selected date
    const dateTimestamp = dateTimeHelpers.createDateTimestamp(year, month, day);
    
    // Check if availability already exists for this date, practice, and operative
    const existingAvailability = await availabilityService.getPracticeAvailability(selectedPractice, dateTimestamp);
    
    const operativeAvailability = existingAvailability.find(
      (avail) => avail.operativeId === selectedOperative
    );
    
    // Prepare slots data
    const slotsData = availabilitySlots.map((slot) => ({
      startTime: slot.startTime,
      endTime: slot.endTime,
      isBooked: !slot.isAvailable,
    }));
    
    if (operativeAvailability) {
      // Update existing availability
      await availabilityService.updateAvailability(operativeAvailability.id, {
        slots: slotsData,
      });
    } else {
      // Create new availability
      await availabilityService.createAvailability({
        practiceId: selectedPractice,
        operativeId: selectedOperative,
        date: dateTimestamp,
        slots: slotsData,
      });
    }
  };
  
  // Save availability for recurring days
  const saveRecurringAvailability = async () => {
    // Parse the starting date string (format: YYYY-MM-DD)
    const [year, month, day] = selectedDate.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);
    
    // Prepare slots data template
    const slotsDataTemplate = availabilitySlots.map((slot) => ({
      startTime: slot.startTime,
      endTime: slot.endTime,
      isBooked: !slot.isAvailable,
    }));
    
    // Create an array of dates to save availability for
    const dates = [];
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    for (let i = 0; i < recurringDays; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      // Check if this day of the week is selected for recurring slots
      const dayOfWeek = daysOfWeek[currentDate.getDay()];
      if (recurringDaysOfWeek[dayOfWeek]) {
        dates.push(currentDate);
      }
    }
    
    // Save availability for each date
    for (const date of dates) {
      const dateTimestamp = dateTimeHelpers.createDateTimestamp(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate()
      );
      
      // Check if availability already exists for this date, practice, and operative
      const existingAvailability = await availabilityService.getPracticeAvailability(selectedPractice, dateTimestamp);
      
      const operativeAvailability = existingAvailability.find(
        (avail) => avail.operativeId === selectedOperative
      );
      
      // Create slots data for this date
      // We need to create new timestamps for each date to avoid reference issues
      const slotsData = slotsDataTemplate.map(templateSlot => {
        const startTime = new Timestamp(
          templateSlot.startTime.seconds,
          templateSlot.startTime.nanoseconds
        );
        const endTime = new Timestamp(
          templateSlot.endTime.seconds,
          templateSlot.endTime.nanoseconds
        );
        
        // Adjust the date part of the timestamps to match the current date
        startTime._seconds += (date.getTime() - startDate.getTime()) / 1000;
        endTime._seconds += (date.getTime() - startDate.getTime()) / 1000;
        
        return {
          startTime,
          endTime,
          isBooked: templateSlot.isBooked,
        };
      });
      
      if (operativeAvailability) {
        // Update existing availability
        await availabilityService.updateAvailability(operativeAvailability.id, {
          slots: slotsData,
        });
      } else {
        // Create new availability
        await availabilityService.createAvailability({
          practiceId: selectedPractice,
          operativeId: selectedOperative,
          date: dateTimestamp,
          slots: slotsData,
        });
      }
    }
  };

  // Get today's date in YYYY-MM-DD format for the date input min attribute
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Availability Management</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      
      <div className="bg-gray-50 p-6 rounded-lg border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Practice Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Practice*
            </label>
            <select
              value={selectedPractice}
              onChange={handlePracticeChange}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select a practice</option>
              {practices.map((practice) => (
                <option key={practice.id} value={practice.id}>
                  {practice.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Operative Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Operative*
            </label>
            <select
              value={selectedOperative}
              onChange={handleOperativeChange}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select an operative</option>
              {operatives.map((operative) => (
                <option key={operative.id} value={operative.id}>
                  {operative.displayName}
                </option>
              ))}
            </select>
          </div>
          
          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Date*
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              min={getTodayDateString()}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
        </div>
        
        {/* Time Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Hour
            </label>
            <select
              value={startHour}
              onChange={(e) => setStartHour(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-md"
            >
              {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
                <option key={hour} value={hour}>
                  {hour > 12 ? hour - 12 : hour} {hour >= 12 ? 'PM' : 'AM'}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Hour
            </label>
            <select
              value={endHour}
              onChange={(e) => setEndHour(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-md"
            >
              {Array.from({ length: 12 }, (_, i) => i + 9).map((hour) => (
                <option key={hour} value={hour}>
                  {hour > 12 ? hour - 12 : hour} {hour >= 12 ? 'PM' : 'AM'}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slot Duration (minutes)
            </label>
            <select
              value={slotDuration}
              onChange={(e) => setSlotDuration(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>
        </div>
        
        {/* Recurring Settings */}
        <div className="mb-6 p-4 border rounded-md bg-blue-50">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="isRecurring"
              checked={isRecurring}
              onChange={() => setIsRecurring(!isRecurring)}
              className="mr-2"
            />
            <label htmlFor="isRecurring" className="font-medium">
              Create Recurring Daily Slots
            </label>
          </div>
          
          {isRecurring && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Days to Create Slots
                </label>
                <select
                  value={recurringDays}
                  onChange={(e) => setRecurringDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {[7, 14, 30, 60, 90].map((days) => (
                    <option key={days} value={days}>
                      {days} days
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days of the Week to Include
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(recurringDaysOfWeek).map(([day, isSelected]) => (
                    <label key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRecurringDayChange(day)}
                        className="mr-2"
                      />
                      <span className="capitalize">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-between">
          <button
            onClick={() => {
              if (selectedPractice && selectedOperative && selectedDate) {
                generateAvailabilitySlots(selectedPractice, selectedOperative, selectedDate);
              } else {
                setError('Please select a practice, operative, and date.');
              }
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            disabled={loading || !selectedPractice || !selectedOperative || !selectedDate}
          >
            {loading ? 'Loading...' : 'Generate Slots'}
          </button>
          
          <button
            onClick={saveAvailability}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
            disabled={loading || availabilitySlots.length === 0}
          >
            {loading ? 'Saving...' : isRecurring ? 'Save Recurring Availability' : 'Save Availability'}
          </button>
        </div>
      </div>
      
      {/* Availability Slots */}
      {availabilitySlots.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-xl font-medium mb-4">Availability Slots</h3>
          <p className="text-gray-600 mb-4">
            Click on a slot to toggle its availability. Green slots are available for booking, red slots are unavailable.
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {availabilitySlots.map((slot) => (
              <div
                key={slot.id}
                onClick={() => toggleSlotAvailability(slot.id)}
                className={`cursor-pointer p-2 rounded text-center ${
                  slot.isAvailable
                    ? 'bg-green-100 hover:bg-green-200 border border-green-300'
                    : 'bg-red-100 hover:bg-red-200 border border-red-300'
                }`}
              >
                <div className="text-sm font-medium">
                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                </div>
                <div className="text-xs mt-1">
                  {slot.isAvailable ? 'Available' : 'Unavailable'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityManagement;
